import React, { useState, useEffect } from 'react';

const CONFERENCE_API_URL = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';
const REGISTRATION_API_URL = process.env.REACT_APP_REGISTRATION_API_URL || 'http://localhost:8085';
const PAPER_API_URL = process.env.REACT_APP_PAPER_API_URL || process.env.REACT_APP_REVIEW_API_URL || 'http://localhost:8082';
const GROUP_REGISTRATION_API_URL = process.env.REACT_APP_GROUP_REGISTRATION_API_URL || 'http://localhost:8086';

const RegistrationList = () => {
  const [conferences, setConferences] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedConference, setSelectedConference] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [registrationIdSearch, setRegistrationIdSearch] = useState('');
  const [alert, setAlert] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showOfflinePaymentModal, setShowOfflinePaymentModal] = useState(false);
  const [offlinePaymentData, setOfflinePaymentData] = useState({
    transactionId: '',
    modeOfPayment: 'CASH',
    amount: '',
    description: '',
    paidOn: ''
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showGroupRegistrationModal, setShowGroupRegistrationModal] = useState(false);
  const [groupRegistrations, setGroupRegistrations] = useState([]);
  const [groupRegistrationSearch, setGroupRegistrationSearch] = useState('');
  const [groupRegistrationLoading, setGroupRegistrationLoading] = useState(false);
  const [groupRegistrationPage, setGroupRegistrationPage] = useState(0);
  const [groupRegistrationTotalPages, setGroupRegistrationTotalPages] = useState(0);
  const [groupRegistrationTotalElements, setGroupRegistrationTotalElements] = useState(0);
  const [selectedSession, setSelectedSession] = useState('all');
  const [availableSessions, setAvailableSessions] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [retryingEmailIds, setRetryingEmailIds] = useState([]);
  const pageSize = 20;
  const groupRegistrationPageSize = 10;

  // Fetch conferences for dropdown
  const fetchConferences = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API_URL}/active`);
      if (response.ok) {
        const data = await response.json();
        setConferences(data);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  // Fetch focused sessions for the selected conference
  const fetchFocusedSessions = async (conferenceId) => {
    setSessionLoading(true);
    try {
      const url = `${CONFERENCE_API_URL}/${encodeURIComponent(conferenceId)}/master/type/FocussedSessions/details`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        // API returns an array of session objects with id and value properties
        const sessions = Array.isArray(data) ? data : [];
        setAvailableSessions(sessions);
        return sessions;
      } else {
        console.error('Failed to fetch focused sessions:', response.status);
        setAvailableSessions([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching focused sessions:', error);
      setAvailableSessions([]);
      return [];
    } finally {
      setSessionLoading(false);
    }
  };

  // Fetch registrations with pagination and status filtering
  const fetchRegistrations = async (conference = 'all', search = '', regIdSearch = '', status = 'all', page = 0, sessionId = 'all') => {
    setLoading(true);
    try {
      let url;
      const baseUrl = REGISTRATION_API_URL.replace(/\/$/, ''); // Remove trailing slash
      
      if (conference === 'all') {
        showAlert('Please select a specific conference', 'error');
        setRegistrations([]);
        setLoading(false);
        return;
      }

      // Build query parameters
      const params = new URLSearchParams();
      if (search && search.trim()) {
        params.append('emailPattern', search.trim());
      }
      if (regIdSearch && regIdSearch.trim()) {
        params.append('registrationId', regIdSearch.trim());
      }
      // Map frontend status to backend status
      if (status !== 'all') {
        const statusMap = {
          'paid': 'SUCCESS',
          'pending': 'PENDING',
          'failed': 'FAILED'
        };
        params.append('paymentStatus', statusMap[status] || status);
      }
      // Add session filter if selected
      if (sessionId !== 'all') {
        params.append('focusedSessionId', sessionId);
      }
      params.append('page', page.toString());
      params.append('size', pageSize.toString());
      
      url = `${baseUrl}/registration/conference/${encodeURIComponent(conference)}/search?${params.toString()}`;

      console.log('Fetching registrations with URL:', url);
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setRegistrations(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setCurrentPage(data.number || 0);
      } else {
        showAlert('Failed to fetch registrations', 'error');
        setRegistrations([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      showAlert('Error fetching registrations', 'error');
      setRegistrations([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConferences();
    // Don't auto-fetch registrations on load
  }, []);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleConferenceChange = (e) => {
    const conferenceName = e.target.value;
    setSelectedConference(conferenceName);
    setSelectedSession('all'); // Reset session filter when conference changes
    setSearchTerm('');
    setRegistrationIdSearch('');
    setFilterStatus('all');
    setCurrentPage(0);
    // Auto-fetch registrations and focused sessions when a specific conference is selected
    if (conferenceName !== 'all') {
      fetchRegistrations(conferenceName, '', '', 'all', 0, 'all');
      // Find the conference ID from the conferences array
      const selectedConf = conferences.find(conf => conf.name === conferenceName);
      if (selectedConf) {
        fetchFocusedSessions(selectedConf.id);
      }
    } else {
      // Clear registrations when "All Conferences" is selected
      setRegistrations([]);
      setTotalPages(0);
      setTotalElements(0);
      setAvailableSessions([]);
    }
  };

  const handleSearch = () => {
    // Validate that a conference is selected
    if (selectedConference === 'all') {
      showAlert('Please select a specific conference to view registrations', 'error');
      return;
    }
    setCurrentPage(0);
    fetchRegistrations(selectedConference, searchTerm, registrationIdSearch, filterStatus, 0, selectedSession);
  };

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;
    setSelectedSession(sessionId);
    setCurrentPage(0);
    // Fetch registrations with the new session filter
    fetchRegistrations(selectedConference, searchTerm, registrationIdSearch, filterStatus, 0, sessionId);
  };

  const handleViewDetails = (registration) => {
    console.log('Selected Registration:', registration);
    if (registration?.additionalFees) {
      console.log('Additional Fees:', registration.additionalFees);
    }
    if (registration?.payment) {
      console.log('Payment Details:', registration.payment);
      registration.payment.forEach((p, idx) => {
        console.log(`Payment ${idx}:`, {
          id: p.id,
          status: p.status,
          amount: p.amount,
          paymentType: p.paymentType,
          razorpayOrderId: p.razorpayOrderId,
          razorpayPaymentId: p.razorpayPaymentId
        });
      });
    }
    setSelectedRegistration(registration);
    setShowDetailsModal(true);
  };

  const handlePrintBadge = async (registration) => {
    // Fetch paper information if exists
    let paperInfo = null;
    try {
      const paperResponse = await fetch(`${PAPER_API_URL}/api/papers/registration/${registration.id}`);
      if (paperResponse.ok) {
        const papers = await paperResponse.json();
        if (papers && papers.length > 0) {
          const paper = papers[0]; // Get the first paper
          // Fetch track information - need conference ID
          try {
            // Find conference ID from conference name
            const conference = conferences.find(c => c.name === registration.conference);
            if (conference) {
              const trackResponse = await fetch(`${CONFERENCE_API_URL}/api/tracks/conference/${conference.id}`);
              if (trackResponse.ok) {
                const tracks = await trackResponse.json();
                const track = tracks.find(t => t.id === paper.trackId);
                if (track) {
                  paperInfo = {
                    title: paper.title,
                    trackName: track.name
                  };
                }
              } else {
                console.warn('Failed to fetch tracks:', trackResponse.status);
              }
            }
          } catch (trackError) {
            console.error('Error fetching track:', trackError);
            // Continue without track info - don't fail badge printing
          }
        }
      }
    } catch (error) {
      console.error('Error fetching paper info:', error);
      // Continue without paper info - don't fail badge printing
    }

    // Create a printable badge layout - always proceed even if paper/track fetch failed
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      showAlert('Please allow pop-ups to print the badge', 'error');
      return;
    }
    
    const badgeHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Badge - ${registration.registrationId || `GR-${registration.id}`}</title>
        <style>
          @page {
            size: 4in 3in;
            margin: 0;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background-color: #f0f0f0;
          }
          .badge {
            width: 4in;
            height: 3in;
            background: white;
            border: 3px solid #2c3e50;
            border-radius: 10px;
            padding: 20px;
            box-sizing: border-box;
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          .badge-header {
            text-align: center;
            border-bottom: 2px solid #3498db;
            padding-bottom: 10px;
          }
          .conference-name {
            font-size: 16px;
            font-weight: bold;
            color: #2c3e50;
            margin: 0;
          }
          .badge-body {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            text-align: center;
            padding: 15px 0;
          }
          .attendee-name {
            font-size: 22px;
            font-weight: bold;
            color: #2c3e50;
            margin: 10px 0;
            text-transform: uppercase;
          }
          .category {
            display: inline-block;
            padding: 5px 15px;
            background-color: #3498db;
            color: white;
            border-radius: 20px;
            font-size: 12px;
            font-weight: bold;
            margin: 10px auto;
          }
          .track-info {
            display: inline-block;
            padding: 4px 12px;
            background-color: #e74c3c;
            color: white;
            border-radius: 15px;
            font-size: 11px;
            font-weight: bold;
            margin: 8px auto;
          }
          .badge-footer {
            text-align: center;
            border-top: 2px solid #3498db;
            padding-top: 8px;
          }
          .reg-id {
            font-size: 11px;
            color: #7f8c8d;
            font-weight: bold;
          }
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 10px 20px;
            background-color: #27ae60;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 14px;
          }
          .print-button:hover {
            background-color: #229954;
          }
        </style>
      </head>
      <body>
        <button class="print-button no-print" onclick="window.print()">🖨️ Print Badge</button>
        <div class="badge">
          <div class="badge-header">
            <p class="conference-name">${registration.conference || 'Conference'}</p>
          </div>
          <div class="badge-body">
            <div class="attendee-name">
              ${registration.user?.firstName || ''} ${registration.user?.lastName || ''}
            </div>
            <div class="category">${registration.category || 'Participant'}</div>
            ${paperInfo ? `<div class="track-info">Track: ${paperInfo.trackName}</div>` : ''}
          </div>
          <div class="badge-footer">
            <div class="reg-id">ID: ${registration.registrationId || `GR-${registration.id}`}</div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(badgeHtml);
    printWindow.document.close();
  };

  // Helper function to get payment status from payment array
  const getPaymentStatus = (registration) => {
    if (!registration.payment || registration.payment.length === 0) {
      return 'PENDING';
    }
    
    // Check if payment is actually an array
    if (!Array.isArray(registration.payment)) {
      // If it's a single object, try to get status from it
      if (typeof registration.payment === 'object' && registration.payment.status) {
        return registration.payment.status;
      }
      return 'PENDING';
    }
    
    // Check if there's a successful payment first
    const successfulPayment = registration.payment.find(p => p.status === 'SUCCESS' || p.status === 'paid');
    if (successfulPayment) {
      return 'SUCCESS';
    }
    
    // Check if there's a failed payment
    const failedPayment = registration.payment.find(p => p.status === 'FAILED');
    if (failedPayment) {
      return 'FAILED';
    }
    
    // Return the status of the most recent payment
    const lastPayment = registration.payment[registration.payment.length - 1];
    return lastPayment?.status || 'PENDING';
  };

  // Helper function to calculate total payment amount
  const getTotalAmount = (registration) => {
    if (!registration.payment || registration.payment.length === 0) {
      return 0;
    }
    
    // Check if payment is actually an array
    if (!Array.isArray(registration.payment)) {
      // If it's a single object, try to get amount from it
      if (typeof registration.payment === 'object' && registration.payment.amount) {
        return registration.payment.amount;
      }
      return 0;
    }
    
    // Sum all payment amounts
    return registration.payment.reduce((total, payment) => {
      const amount = payment.amount || 0;
      return total + parseFloat(amount);
    }, 0);
  };

  const getSuccessfulPayment = (registration) => {
    if (!registration.payment) {
      return null;
    }

    if (!Array.isArray(registration.payment)) {
      return registration.payment.status === 'SUCCESS' || registration.payment.status === 'paid'
        ? registration.payment
        : null;
    }

    return registration.payment.find(
      (payment) => payment.status === 'SUCCESS' || payment.status === 'paid'
    ) || null;
  };

  const handleRetryConfirmationEmail = async (registration) => {
    const payment = getSuccessfulPayment(registration);
    const transactionId =
      payment?.razorpayPaymentId ||
      payment?.transactionId ||
      payment?.razorpayOrderId ||
      payment?.id;

    if (!registration?.id) {
      showAlert('Cannot retry email: registration id is missing', 'error');
      return;
    }

    if (!transactionId) {
      showAlert('Cannot retry email: payment transaction id is missing', 'error');
      return;
    }

    setRetryingEmailIds((prev) => [...prev, registration.id]);

    try {
      const response = await fetch(`${REGISTRATION_API_URL}/payment/retryEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          regId: registration.id,
          transactionId: String(transactionId)
        })
      });

      const responseText = await response.text();
      let responseBody = null;
      try {
        responseBody = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        responseBody = null;
      }

      if (response.ok && responseBody?.success !== false) {
        showAlert(responseBody?.message || 'Confirmation email retry queued', 'success');
      } else {
        showAlert(
          responseBody?.message || responseText || 'Failed to retry confirmation email',
          'error'
        );
      }
    } catch (error) {
      console.error('Error retrying confirmation email:', error);
      showAlert('Error retrying confirmation email', 'error');
    } finally {
      setRetryingEmailIds((prev) => prev.filter((id) => id !== registration.id));
    }
  };

  // Helper function to format currency
  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleFilterStatusChange = (e) => {
    const newStatus = e.target.value;
    setFilterStatus(newStatus);
    setCurrentPage(0);
    if (selectedConference !== 'all') {
      fetchRegistrations(selectedConference, searchTerm, registrationIdSearch, newStatus, 0, selectedSession);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      fetchRegistrations(selectedConference, searchTerm, registrationIdSearch, filterStatus, newPage, selectedSession);
    }
  };

  const fetchGroupRegistrations = async (conferenceName, page = 0, search = '') => {
    if (!conferenceName || conferenceName === 'all') {
      setGroupRegistrations([]);
      setGroupRegistrationTotalElements(0);
      setGroupRegistrationTotalPages(0);
      setGroupRegistrationPage(0);
      return;
    }

    setGroupRegistrationLoading(true);
    try {
      const params = new URLSearchParams({
        conference: conferenceName,
        page: String(page),
        size: String(groupRegistrationPageSize)
      });
      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await fetch(`${GROUP_REGISTRATION_API_URL}/group-registration/registrations/by-conference/paged?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch group registrations');
      }

      const data = await response.json();
      setGroupRegistrations(Array.isArray(data.content) ? data.content : []);
      setGroupRegistrationPage(data.number || 0);
      setGroupRegistrationTotalPages(data.totalPages || 0);
      setGroupRegistrationTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error('Error fetching group registrations:', error);
      showAlert('Failed to load group registrations', 'error');
      setGroupRegistrations([]);
      setGroupRegistrationPage(0);
      setGroupRegistrationTotalPages(0);
      setGroupRegistrationTotalElements(0);
    } finally {
      setGroupRegistrationLoading(false);
    }
  };

  const handleOpenGroupRegistrationModal = () => {
    if (selectedConference === 'all') {
      showAlert('Please select a specific conference first', 'error');
      return;
    }

    setGroupRegistrationSearch('');
    setShowGroupRegistrationModal(true);
    fetchGroupRegistrations(selectedConference, 0, '');
  };

  const handleGroupRegistrationSearch = () => {
    fetchGroupRegistrations(selectedConference, 0, groupRegistrationSearch);
  };

  const handleGroupRegistrationPageChange = (newPage) => {
    if (newPage < 0 || newPage >= groupRegistrationTotalPages) {
      return;
    }
    fetchGroupRegistrations(selectedConference, newPage, groupRegistrationSearch);
  };

  const handleViewGroupRegistration = (groupRegistration) => {
    handleViewDetails({
      ...groupRegistration,
      registrationId: groupRegistration.registrationId || `GR-${groupRegistration.id}`,
      category: groupRegistration.category || 'Group Participant',
      registrationType: groupRegistration.registrationType || 'GROUP',
      _isGroupRegistration: true
    });
  };

  const handlePrintGroupRegistration = (groupRegistration) => {
    handlePrintBadge({
      ...groupRegistration,
      registrationId: groupRegistration.registrationId || `GR-${groupRegistration.id}`,
      category: groupRegistration.category || 'Group Participant'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'SUCCESS': { bg: '#d4edda', color: '#155724' },
      'FAILED': { bg: '#f8d7da', color: '#721c24' },
      'PENDING': { bg: '#fff3cd', color: '#856404' },
      'CREATED': { bg: '#d1ecf1', color: '#0c5460' }
    };

    const style = statusColors[status] || statusColors['PENDING'];
    
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {status || 'PENDING'}
      </span>
    );
  };

  const handleAddOfflinePayment = (registration) => {
    setSelectedRegistration(registration);
    setOfflinePaymentData({
      transactionId: '',
      modeOfPayment: 'UPI',
      amount: '',
      description: '',
      paidOn: new Date().toISOString().split('T')[0] // Default to today
    });
    setShowOfflinePaymentModal(true);
  };

  const handleOfflinePaymentSubmit = async () => {
    if (!offlinePaymentData.transactionId || !offlinePaymentData.amount || !offlinePaymentData.paidOn) {
      showAlert('Please fill in all required fields', 'error');
      return;
    }

    try {
      const response = await fetch(`${REGISTRATION_API_URL}/payment/offline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registrationId: selectedRegistration.id,
          transactionId: offlinePaymentData.transactionId,
          modeOfPayment: offlinePaymentData.modeOfPayment,
          amount: parseFloat(offlinePaymentData.amount),
          description: offlinePaymentData.description,
          paidOn: offlinePaymentData.paidOn
        })
      });

      if (response.ok) {
        showAlert('Offline payment added successfully', 'success');
        setShowOfflinePaymentModal(false);
        setShowDetailsModal(false);
        // Refresh the registrations list
        fetchRegistrations(selectedConference, searchTerm, registrationIdSearch, filterStatus, currentPage, selectedSession);
      } else {
        const error = await response.text();
        showAlert(`Failed to add offline payment: ${error}`, 'error');
      }
    } catch (error) {
      console.error('Error adding offline payment:', error);
      showAlert('Error adding offline payment', 'error');
    }
  };

  const formatAdditionalDataForCsv = (additionalData) => {
    if (!additionalData || Object.keys(additionalData).length === 0) {
      return '-';
    }

    try {
      return JSON.stringify(additionalData);
    } catch (error) {
      console.error('Error formatting additional data for CSV:', error);
      return String(additionalData);
    }
  };

  const normalizeId = (value) => (value === null || value === undefined ? '' : String(value));

  const getAdditionalFeeName = (fee) => fee?.name || fee?.personName || '-';

  const getAdditionalFeeAmount = (registration, fee) => {
    const payment = registration.payment?.find(pay => normalizeId(pay.id) === normalizeId(fee.paymentId));
    const paymentAmount = Number(payment?.amount);
    const feeRate = Number(fee?.rate);

    if (Number.isFinite(paymentAmount) && paymentAmount > 0) {
      return paymentAmount;
    }
    if (Number.isFinite(feeRate)) {
      return feeRate;
    }
    return 0;
  };

  const formatAdditionalFeeEntries = (registration, fees) => {
    return fees.map(fee => {
      const amount = getAdditionalFeeAmount(registration, fee);
      return `${getAdditionalFeeName(fee)}: INR ${amount.toFixed(2)}`;
    }).join('; ');
  };

  const handleDownloadCSV = async () => {
    if (selectedConference === 'all') {
      showAlert('Please select a specific conference to download registrations', 'error');
      return;
    }

    if (totalElements === 0) {
      showAlert('No registrations to download', 'error');
      return;
    }

    // Show loading state
    showAlert(`Preparing download of ${totalElements} registrations...`, 'success');

    try {
      // Fetch ALL registrations (not just current page)
      const baseUrl = REGISTRATION_API_URL.replace(/\/$/, '');
      const params = new URLSearchParams();
      
      if (searchTerm && searchTerm.trim()) {
        params.append('emailPattern', searchTerm.trim());
      }
      if (registrationIdSearch && registrationIdSearch.trim()) {
        params.append('registrationId', registrationIdSearch.trim());
      }
      if (filterStatus !== 'all') {
        const statusMap = {
          'paid': 'SUCCESS',
          'pending': 'PENDING',
          'failed': 'FAILED'
        };
        params.append('paymentStatus', statusMap[filterStatus] || filterStatus);
      }
      
      // Fetch all records in one call
      params.append('page', '0');
      params.append('size', totalElements.toString());
      
      const url = `${baseUrl}/registration/conference/${encodeURIComponent(selectedConference)}/search?${params.toString()}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        showAlert('Failed to fetch all registrations for download', 'error');
        return;
      }
      
      const data = await response.json();
      const allRegistrations = data.content || [];

      if (allRegistrations.length === 0) {
        showAlert('No registrations to download', 'error');
        return;
      }

      let focusedSessionsForExport = availableSessions;
      if (!focusedSessionsForExport.length) {
        const selectedConf = conferences.find(conf => conf.name === selectedConference);
        if (selectedConf) {
          focusedSessionsForExport = await fetchFocusedSessions(selectedConf.id);
        }
      }

      const focusedSessionFeeTypeIds = new Set(
        focusedSessionsForExport.map(session => normalizeId(session.id))
      );

      // Define CSV headers
      const headers = [
        'Registration ID',
        'Title',
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Gender',
        'Country',
        'State',
        'City',
        'Pincode',
        'Address',
        'Conference',
        'Category',
        'Registration Type',
        'Department',
        'Affiliation',
        'Institution',
        'MCI Registered',
        'MCI Number',
        'ISA Membership Number',
        'Additional Data',
        'Registered On',
        'Accompanying Persons Count',
        'Accompanying Person Names',
        'Accompanying Person Fees',
        'Workshop / Focussed Session Names',
        'Total Amount',
        'Payment Status',
        'Mode of Payment',
        'Transaction ID',
        'Payment Date',
        'Payment Description'
      ];

      // Prepare CSV rows
      const rows = allRegistrations.map(reg => {
      const additionalFees = Array.isArray(reg.additionalFees) ? reg.additionalFees : [];
      const isWorkshopFee = (fee) => focusedSessionFeeTypeIds.has(normalizeId(fee.feeTypeId));

      const workshopFees = additionalFees.filter(isWorkshopFee);

      // additional_fee.name is exposed as personName for backward compatibility.
      // Use feeTypeId parent/type to avoid classifying workshops as accompanying persons.
      const accompanyingPersons = additionalFees.filter(fee =>
        !isWorkshopFee(fee) && getAdditionalFeeName(fee).trim() !== ''
      );

      const accompanyingPersonNames = accompanyingPersons.map(getAdditionalFeeName).join('; ');
      const accompanyingPersonFees = formatAdditionalFeeEntries(reg, accompanyingPersons);
      const workshopNames = workshopFees.map(getAdditionalFeeName).join('; ');

      // Get payment information (most recent or successful payment)
      let paymentInfo = {
        status: 'PENDING',
        type: '-',
        transactionId: '-',
        razorpayOrderId: '-',
        razorpayPaymentId: '-',
        paymentDate: '-',
        description: '-',
        modeOfPayment: '-'
      };

      if (reg.payment && reg.payment.length > 0) {
        // Find successful payment or use most recent
        const successfulPayment = reg.payment.find(p => p.status === 'SUCCESS' || p.status === 'paid');
        const payment = successfulPayment || reg.payment[reg.payment.length - 1];
        
        paymentInfo = {
          status: payment.status || 'PENDING',
          type: payment.paymentType || '-',
          transactionId: payment.razorpayPaymentId || payment.transactionId || '-',
          razorpayOrderId: payment.razorpayOrderId || '-',
          razorpayPaymentId: payment.razorpayPaymentId || '-',
          paymentDate: payment.paymentDate ? new Date(payment.paymentDate).toLocaleString() : 
                       payment.paidOn ? new Date(payment.paidOn).toLocaleString() : '-',
          description: payment.description || '-',
          modeOfPayment: payment.paymentType || '-'
        };
      }

      // Calculate total amount
      const totalAmount = getTotalAmount(reg);

      return [
        reg.registrationId || '-',
        reg.user?.title || '-',
        reg.user?.firstName || '-',
        reg.user?.lastName || '-',
        reg.user?.email || '-',
        reg.user?.phone || '-',
        reg.user?.gender || '-',
        reg.user?.country || '-',
        reg.user?.state || '-',
        reg.user?.city || '-',
        reg.user?.zipCode || '-',
        reg.user?.address || '-',
        reg.conference || '-',
        reg.category || '-',
        reg.registrationType || '-',
        reg.department || '-',
        reg.affiliation || '-',
        reg.institution || '-',
        reg.mciRegistered || '-',
        reg.mciNumber || '-',
        reg.isaMembershipNumber || '-',
        formatAdditionalDataForCsv(reg.additionalData),
        reg.updatedOn ? new Date(reg.updatedOn).toLocaleString() : (reg.registeredOn ? new Date(reg.registeredOn).toLocaleString() : '-'),
        accompanyingPersons.length || '0',
        accompanyingPersonNames || '-',
        accompanyingPersonFees || '-',
        workshopNames || '-',
        totalAmount.toFixed(2),
        paymentInfo.status,
        paymentInfo.modeOfPayment,
        paymentInfo.transactionId,
        paymentInfo.paymentDate,
        paymentInfo.description
      ];
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => {
          // Escape commas, quotes, and newlines in cell content
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const downloadUrl = URL.createObjectURL(blob);
    
    // Generate filename with conference name, status filter, and timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const statusSuffix = filterStatus !== 'all' ? `_${filterStatus}` : '';
    const filename = `registrations_${selectedConference.replace(/[^a-z0-9]/gi, '_')}${statusSuffix}_${timestamp}.csv`;
    
    link.setAttribute('href', downloadUrl);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

      showAlert(`Successfully downloaded ${allRegistrations.length} registrations`, 'success');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      showAlert('Error downloading registrations', 'error');
    }
  };

  return (
    <div>
      <div className="content-header">
        <h1>Registration Management</h1>
        <div className="breadcrumb">Admin / Registrations</div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          <span>{alert.message}</span>
        </div>
      )}

      <div className="content-body">
        <div className="action-bar" style={{ flexWrap: 'wrap', gap: '10px' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '250px' }}>
            <select
              value={selectedConference}
              onChange={handleConferenceChange}
              style={{ padding: '10px', fontSize: '14px' }}
            >
              <option value="all">All Conferences</option>
              {conferences.map((conf) => (
                <option key={conf.id} value={conf.name}>
                  {conf.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0, minWidth: '250px' }}>
            <select
              value={selectedSession}
              onChange={handleSessionChange}
              disabled={selectedConference === 'all' || availableSessions.length === 0}
              style={{ padding: '10px', fontSize: '14px', opacity: (selectedConference === 'all' || availableSessions.length === 0) ? 0.5 : 1 }}
            >
              <option value="all">All Sessions/Workshops</option>
              {availableSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.value}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
            <select
              value={filterStatus}
              onChange={handleFilterStatusChange}
              style={{ padding: '10px', fontSize: '14px' }}
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="search-box" style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <div className="search-box" style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="Search by Registration ID..."
              value={registrationIdSearch}
              onChange={(e) => setRegistrationIdSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <div className="action-buttons" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button className="btn btn-secondary" onClick={handleSearch}>
              🔍 Search
            </button>
          </div>

          <div className="action-buttons" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {selectedConference !== 'all' && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {registrations.length > 0 && (
                  <button
                    className="btn btn-primary"
                    onClick={handleDownloadCSV}
                    title="Download all registrations as CSV"
                  >
                    📥 Download CSV
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={handleOpenGroupRegistrationModal}
                  title="View group registrations"
                >
                  👥 Group Registration
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '15px', color: '#7f8c8d', fontSize: '14px' }}>
          Showing {registrations.length} of {totalElements} registrations
          {filterStatus !== 'all' && ` (${filterStatus} only)`}
          {selectedSession !== 'all' && ` for session ${availableSessions.find(s => s.id == selectedSession)?.value || selectedSession}`}
          {selectedConference !== 'all' && ` for ${conferences.find(c => c.name === selectedConference)?.name || selectedConference}`}
          {totalPages > 1 && ` - Page ${currentPage + 1} of ${totalPages}`}
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : selectedConference === 'all' ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3>Select a Conference</h3>
            <p>Please select a specific conference from the dropdown above to view registrations</p>
          </div>
        ) : registrations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <h3>No Registrations Found</h3>
            <p>
              {searchTerm || registrationIdSearch
                ? 'No registrations match your search criteria'
                : 'There are no registrations available for this conference'}
            </p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Registration ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Conference</th>
                  <th>Category</th>
                  <th>Registered On</th>
                  <th>Total Amount</th>
                  <th>Payment Type</th>
                  <th>Payment Status</th>
                  <th>Badge</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((reg) => (
                  <tr key={reg.id}>
                    <td><strong>{reg.registrationId}</strong></td>
                    <td>{reg.user?.firstName} {reg.user?.lastName}</td>
                    <td>{reg.user?.email}</td>
                    <td>{reg.conference}</td>
                    <td>{reg.category}</td>
                    <td>
                      {reg.registeredOn
                        ? new Date(reg.registeredOn).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <strong style={{ color: '#2c3e50' }}>
                        {formatCurrency(getTotalAmount(reg))}
                      </strong>
                    </td>
                    <td>
                      {reg.payment && reg.payment.length > 0 && reg.payment[0]?.paymentType ? (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          backgroundColor: reg.payment[0].paymentType === 'ONLINE' ? '#e3f2fd' : '#fff3e0',
                          color: reg.payment[0].paymentType === 'ONLINE' ? '#1976d2' : '#f57c00'
                        }}>
                          {reg.payment[0].paymentType}
                        </span>
                      ) : '-'}
                    </td>
                    <td>{getStatusBadge(getPaymentStatus(reg))}</td>
                    <td>
                      {getPaymentStatus(reg) === 'SUCCESS' ? (
                        <button
                          className="icon-btn"
                          onClick={() => handlePrintBadge(reg)}
                          title="Print Badge"
                          style={{ 
                            color: '#27ae60',
                            fontSize: '20px',
                            padding: '5px 10px'
                          }}
                        >
                          🖨️
                        </button>
                      ) : (
                        <span style={{ color: '#95a5a6', fontSize: '12px' }}>-</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="icon-btn"
                        onClick={() => handleViewDetails(reg)}
                        title="View Details"
                        style={{ color: '#3498db' }}
                      >
                        👁️
                      </button>
                      {getPaymentStatus(reg) === 'SUCCESS' && (
                        <button
                          className="icon-btn"
                          onClick={() => handleRetryConfirmationEmail(reg)}
                          title="Retry confirmation email"
                          disabled={retryingEmailIds.includes(reg.id)}
                          style={{
                            color: retryingEmailIds.includes(reg.id) ? '#95a5a6' : '#8e44ad',
                            marginLeft: '8px',
                            opacity: retryingEmailIds.includes(reg.id) ? 0.6 : 1,
                            cursor: retryingEmailIds.includes(reg.id) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {retryingEmailIds.includes(reg.id) ? '⏳' : '✉️'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '15px', 
                marginTop: '20px',
                padding: '15px'
              }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  style={{ opacity: currentPage === 0 ? 0.5 : 1 }}
                >
                  ← Previous
                </button>
                <span style={{ color: '#2c3e50', fontWeight: '500' }}>
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button 
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  style={{ opacity: currentPage >= totalPages - 1 ? 0.5 : 1 }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRegistration && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)} style={{ zIndex: selectedRegistration._isGroupRegistration ? 2000 : 1000 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>Registration Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {/* Basic Information (Following Excel Order) */}
                <div>
                  <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '5px' }}>
                    Registration & Personal Details
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <strong>Registration ID:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.registrationId}</div>
                    </div>
                    <div>
                      <strong>Title:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.title || '-'}</div>
                    </div>
                    <div>
                      <strong>First Name:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.firstName || '-'}</div>
                    </div>
                    <div>
                      <strong>Last Name:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.lastName || '-'}</div>
                    </div>
                    <div>
                      <strong>Email:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.email}</div>
                    </div>
                    <div>
                      <strong>Phone:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.phone || '-'}</div>
                    </div>
                    {selectedRegistration.user?.gender && (
                      <div>
                        <strong>Gender:</strong>
                        <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user.gender}</div>
                      </div>
                    )}
                    <div>
                      <strong>Country:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.country || '-'}</div>
                    </div>
                    {selectedRegistration.user?.state && (
                      <div>
                        <strong>State:</strong>
                        <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user.state}</div>
                      </div>
                    )}
                    <div>
                      <strong>City:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.city || '-'}</div>
                    </div>
                    {selectedRegistration.user?.zipCode && (
                      <div>
                        <strong>Pincode:</strong>
                        <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user.zipCode}</div>
                      </div>
                    )}
                    <div>
                      <strong>Address:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.user?.address || '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Registration & Professional Details */}
                <div>
                  <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #27ae60', paddingBottom: '5px' }}>
                    Conference & Professional Details
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <strong>Category:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.category}</div>
                    </div>
                    <div>
                      <strong>Registration Type:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.registrationType}</div>
                    </div>
                    {selectedRegistration.isaMembershipNumber && (
                      <div>
                        <strong>ISA Membership Number:</strong>
                        <div style={{ color: '#7f8c8d' }}>{selectedRegistration.isaMembershipNumber}</div>
                      </div>
                    )}
                    <div>
                      <strong>Affiliation:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.affiliation || '-'}</div>
                    </div>
                    <div>
                      <strong>Institution:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.institution || '-'}</div>
                    </div>
                    <div>
                      <strong>Department:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.department || '-'}</div>
                    </div>
                    <div>
                      <strong>MCI Registered:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.mciRegistered || '-'}</div>
                    </div>
                    {selectedRegistration.mciNumber && (
                      <div>
                        <strong>MCI Number:</strong>
                        <div style={{ color: '#7f8c8d' }}>{selectedRegistration.mciNumber}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Accompanying Persons */}
                {selectedRegistration.additionalFees && selectedRegistration.additionalFees.length > 0 && (
                  <div>
                    <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #e74c3c', paddingBottom: '5px' }}>
                      Accompanying Persons
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <strong>Number of Accompanying Persons:</strong>
                        <div style={{ color: '#7f8c8d' }}>{selectedRegistration.additionalFees.length}</div>
                      </div>
                      <div>
                        <strong>Accompanying Person Names:</strong>
                        <div style={{ color: '#7f8c8d' }}>
                          {selectedRegistration.additionalFees.map((fee, idx) => (
                            <div key={idx}>{idx + 1}. {fee.personName}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Conference & Registration Dates */}
                <div>
                  <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #f39c12', paddingBottom: '5px' }}>
                    Conference Information
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <strong>Conference:</strong>
                      <div style={{ color: '#7f8c8d' }}>{selectedRegistration.conference}</div>
                    </div>
                    <div>
                      <strong>Registered On:</strong>
                      <div style={{ color: '#7f8c8d' }}>
                        {selectedRegistration.registeredOn 
                          ? new Date(selectedRegistration.registeredOn).toLocaleString()
                          : '-'}
                      </div>
                    </div>
                    {selectedRegistration.membershipNumber && (
                      <div>
                        <strong>Membership Number:</strong>
                        <div style={{ color: '#7f8c8d' }}>{selectedRegistration.membershipNumber}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Information */}
                <div>
                  <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #9b59b6', paddingBottom: '5px' }}>
                    Payment Information {selectedRegistration._isGroupRegistration && '(Conference Group)'}
                  </h3>
                  {selectedRegistration._isGroupRegistration ? (
                    <div className="empty-state" style={{ padding: '20px', backgroundColor: '#e8f4f8', borderRadius: '6px', border: '1px solid #b3dce6' }}>
                      <p style={{ margin: 0, color: '#0c5460', fontWeight: '600' }}>📋 Group Payment Information</p>
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#0c7490' }}>
                        Payment information is managed at the conference group level. This employee's registration is part of the group payment.
                      </p>
                    </div>
                  ) : selectedRegistration.payment && selectedRegistration.payment.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {selectedRegistration.payment.map((paymentItem, index) => (
                        <div 
                          key={paymentItem.id || index}
                          style={{ 
                            padding: '12px',
                            backgroundColor: paymentItem.status === 'paid' || paymentItem.status === 'SUCCESS' ? '#d4edda' : '#f8d7da',
                            borderRadius: '6px',
                            border: `1px solid ${paymentItem.status === 'paid' || paymentItem.status === 'SUCCESS' ? '#c3e6cb' : '#f5c6cb'}`
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                              <strong>Status:</strong>
                              <div style={{ marginTop: '4px' }}>{getStatusBadge(paymentItem.status)}</div>
                            </div>
                            {paymentItem.paymentType && (
                              <div>
                                <strong>Payment Type:</strong>
                                <div style={{ 
                                  display: 'inline-block',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  backgroundColor: paymentItem.paymentType === 'ONLINE' ? '#e3f2fd' : '#fff3e0',
                                  color: paymentItem.paymentType === 'ONLINE' ? '#1976d2' : '#f57c00',
                                  marginTop: '4px'
                                }}>
                                  {paymentItem.paymentType}
                                </div>
                              </div>
                            )}
                            {paymentItem.description && (
                              <div>
                                <strong>Description:</strong>
                                <div style={{ color: '#2c3e50', fontSize: '14px' }}>
                                  {paymentItem.description}
                                </div>
                              </div>
                            )}
                            <div>
                              <strong>Amount:</strong>
                              <div style={{ color: '#2c3e50', fontSize: '16px', fontWeight: '600' }}>
                                ₹{paymentItem.amount?.toFixed(2) || '0.00'}
                              </div>
                            </div>
                            {paymentItem.razorpayPaymentId && (
                              <div>
                                <strong>Payment ID:</strong>
                                <div style={{ color: '#7f8c8d', fontSize: '11px', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                  {paymentItem.razorpayPaymentId}
                                </div>
                              </div>
                            )}
                            {paymentItem.razorpayOrderId && (
                              <div>
                                <strong>Order ID:</strong>
                                <div style={{ color: '#7f8c8d', fontSize: '11px', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                  {paymentItem.razorpayOrderId}
                                </div>
                              </div>
                            )}
                            {paymentItem.transactionId && (
                              <div>
                                <strong>Transaction ID:</strong>
                                <div style={{ color: '#7f8c8d', fontSize: '12px', fontFamily: 'monospace' }}>
                                  {paymentItem.transactionId}
                                </div>
                              </div>
                            )}
                            {paymentItem.modeOfPayment && (
                              <div>
                                <strong>Mode of Payment:</strong>
                                <div style={{ color: '#2c3e50', fontSize: '12px' }}>
                                  {paymentItem.modeOfPayment.replace(/_/g, ' ')}
                                </div>
                              </div>
                            )}
                            {paymentItem.paidOn && (
                              <div>
                                <strong>Paid On:</strong>
                                <div style={{ color: '#7f8c8d' }}>
                                  {new Date(paymentItem.paidOn).toLocaleDateString()}
                                </div>
                              </div>
                            )}
                            {paymentItem.paymentDate && (
                              <div>
                                <strong>Payment Date:</strong>
                                <div style={{ color: '#7f8c8d' }}>
                                  {new Date(paymentItem.paymentDate).toLocaleString()}
                                </div>
                              </div>
                            )}
                          </div>
                          {selectedRegistration.payment.length > 1 && (
                            <div style={{ 
                              marginTop: '8px', 
                              paddingTop: '8px', 
                              borderTop: '1px solid rgba(0,0,0,0.1)',
                              fontSize: '12px',
                              color: '#7f8c8d'
                            }}>
                              Payment {index + 1} of {selectedRegistration.payment.length}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state" style={{ padding: '20px' }}>
                      <p style={{ margin: 0, color: '#e74c3c' }}>⚠️ No payment information available</p>
                      <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#7f8c8d' }}>
                        This registration has not been paid yet
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {getPaymentStatus(selectedRegistration) === 'PENDING' && !selectedRegistration._isGroupRegistration && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleAddOfflinePayment(selectedRegistration)}
                  style={{ marginRight: '10px' }}
                >
                  Add Offline Payment
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Registration Modal */}
      {showGroupRegistrationModal && (
        <div className="modal-overlay" onClick={() => setShowGroupRegistrationModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1100px' }}>
            <div className="modal-header">
              <h2>Group Registration Employees</h2>
              <button className="modal-close" onClick={() => setShowGroupRegistrationModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <div style={{ color: '#7f8c8d', fontSize: '14px' }}>
                  Conference: <strong>{selectedConference}</strong> | Showing {groupRegistrations.length} of {groupRegistrationTotalElements}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="Search by email, name, phone, ID"
                    value={groupRegistrationSearch}
                    onChange={(e) => setGroupRegistrationSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleGroupRegistrationSearch()}
                    style={{ minWidth: '260px', padding: '8px 10px' }}
                  />
                  <button className="btn btn-secondary" onClick={handleGroupRegistrationSearch}>Search</button>
                </div>
              </div>

              {groupRegistrationLoading ? (
                <div className="loading"><div className="spinner"></div></div>
              ) : groupRegistrations.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px' }}>
                  <div className="empty-state-icon">👥</div>
                  <h3>No Group Employees Found</h3>
                  <p>{groupRegistrationSearch ? 'No matching group employees found for your search.' : 'No group registrations found for this conference.'}</p>
                </div>
              ) : (
                <>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Registration ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Gender</th>
                        <th>Conference</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupRegistrations.map((reg, idx) => (
                        <tr key={`group-reg-${reg.id}`}>
                          <td>{groupRegistrationPage * groupRegistrationPageSize + idx + 1}</td>
                          <td><strong>{reg.registrationId || `GR-${reg.id}`}</strong></td>
                          <td>{reg.user?.firstName || '-'} {reg.user?.lastName || ''}</td>
                          <td>{reg.user?.email || '-'}</td>
                          <td>{reg.user?.phone || '-'}</td>
                          <td>{reg.user?.gender || '-'}</td>
                          <td>{reg.conference || selectedConference}</td>
                          <td>
                            <button
                              className="icon-btn"
                              onClick={() => handleViewGroupRegistration(reg)}
                              title="View Details"
                              style={{ color: '#3498db' }}
                            >
                              👁️
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => handlePrintGroupRegistration(reg)}
                              title="Print Badge"
                              style={{ color: '#27ae60' }}
                            >
                              🖨️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {groupRegistrationTotalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '15px',
                      marginTop: '20px',
                      padding: '15px'
                    }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleGroupRegistrationPageChange(groupRegistrationPage - 1)}
                        disabled={groupRegistrationPage === 0}
                        style={{ opacity: groupRegistrationPage === 0 ? 0.5 : 1 }}
                      >
                        ← Previous
                      </button>
                      <span style={{ color: '#2c3e50', fontWeight: '500' }}>
                        Page {groupRegistrationPage + 1} of {groupRegistrationTotalPages}
                      </span>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleGroupRegistrationPageChange(groupRegistrationPage + 1)}
                        disabled={groupRegistrationPage >= groupRegistrationTotalPages - 1}
                        style={{ opacity: groupRegistrationPage >= groupRegistrationTotalPages - 1 ? 0.5 : 1 }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowGroupRegistrationModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline Payment Modal */}
      {showOfflinePaymentModal && selectedRegistration && (
        <div className="modal-overlay" onClick={() => setShowOfflinePaymentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Add Offline Payment</h2>
              <button className="modal-close" onClick={() => setShowOfflinePaymentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                  <strong>Registration ID:</strong>
                  <div style={{ color: '#7f8c8d', marginTop: '5px' }}>{selectedRegistration.registrationId}</div>
                </div>
                <div>
                  <strong>Participant:</strong>
                  <div style={{ color: '#7f8c8d', marginTop: '5px' }}>
                    {selectedRegistration.user?.firstName} {selectedRegistration.user?.lastName}
                  </div>
                </div>

                <div className="form-group">
                  <label><strong>Transaction ID *</strong></label>
                  <input
                    type="text"
                    value={offlinePaymentData.transactionId}
                    onChange={(e) => setOfflinePaymentData({ ...offlinePaymentData, transactionId: e.target.value })}
                    placeholder="Enter transaction ID"
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>

                <div className="form-group">
                  <label><strong>Mode of Payment *</strong></label>
                  <select
                    value={offlinePaymentData.modeOfPayment}
                    onChange={(e) => setOfflinePaymentData({ ...offlinePaymentData, modeOfPayment: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  >
                    <option value="UPI">UPI</option>
                    <option value="DEBIT_CARD">Debit Card</option>
                    <option value="CREDIT_CARD">Credit Card</option>
                    <option value="NET_BANKING">Net Banking</option>
                  </select>
                </div>

                <div className="form-group">
                  <label><strong>Amount (INR) *</strong></label>
                  <input
                    type="number"
                    step="0.01"
                    value={offlinePaymentData.amount}
                    onChange={(e) => setOfflinePaymentData({ ...offlinePaymentData, amount: e.target.value })}
                    placeholder="Enter amount"
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>

                <div className="form-group">
                  <label><strong>Paid On *</strong></label>
                  <input
                    type="date"
                    value={offlinePaymentData.paidOn}
                    onChange={(e) => setOfflinePaymentData({ ...offlinePaymentData, paidOn: e.target.value })}
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>

                <div className="form-group">
                  <label><strong>Description</strong></label>
                  <textarea
                    value={offlinePaymentData.description}
                    onChange={(e) => setOfflinePaymentData({ ...offlinePaymentData, description: e.target.value })}
                    placeholder="Enter payment description"
                    rows="3"
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleOfflinePaymentSubmit}>
                Submit Payment
              </button>
              <button className="btn btn-secondary" onClick={() => setShowOfflinePaymentModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationList;
